/**
 * Phase 735.56 — Settings → Backups & Restore.
 *
 * Phase 1 UI:
 *   • Dropbox status + Connect / Disconnect flow (admin credentials form).
 *   • Manual "Backup Now" button (streams status to a small progress area).
 *   • Backup History table (name, date, size, status, download / delete).
 *   • Audit log preview.
 *
 * The App Key / App Secret submitted here are POSTed to the backend and
 * NEVER echoed back or stored in the browser. The OAuth flow uses a
 * server-issued authorize URL — the frontend just window.location.href's
 * onto it.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CloudArrowUp, PlugsConnected, Plugs, Trash, ArrowClockwise, Download } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });

const fmtBytes = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { const d = new Date(iso); return d.toLocaleString(); } catch { return iso; }
};

export default function BackupsPage() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [backups, setBackups] = useState([]);
  const [running, setRunning] = useState(false);
  const [audit, setAudit] = useState([]);

  const refreshStatus = async () => {
    try {
      const [s, list, log] = await Promise.all([
        axios.get(`${API}/integrations/dropbox/status`, axCfg()),
        axios.get(`${API}/backups/list`, axCfg()),
        axios.get(`${API}/backups/audit-log`, axCfg()),
      ]);
      setStatus(s.data);
      setBackups(list.data.backups || []);
      setAudit(log.data.entries || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Admin access required.');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load Backups page');
      }
    }
    setLoadingStatus(false);
  };

  useEffect(() => { refreshStatus(); }, []);

  // Detect ?dropbox=connected|error from OAuth callback redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dbx = params.get('dropbox');
    if (dbx === 'connected') {
      toast.success('Dropbox connected successfully.');
      window.history.replaceState({}, '', window.location.pathname);
      refreshStatus();
    } else if (dbx === 'error') {
      toast.error(`Dropbox connection failed: ${params.get('reason') || 'unknown'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const saveCreds = async () => {
    if (!appKey.trim() || !appSecret.trim()) {
      toast.error('Provide both App Key and App Secret'); return;
    }
    setSavingCreds(true);
    try {
      await axios.post(`${API}/integrations/dropbox/settings`,
        { app_key: appKey.trim(), app_secret: appSecret.trim() }, axCfg());
      toast.success('Dropbox app credentials saved (encrypted).');
      setAppSecret('');
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save credentials');
    }
    setSavingCreds(false);
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/integrations/dropbox/connect`, axCfg());
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot start OAuth');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Dropbox? Scheduled backups will stop until you reconnect.')) return;
    try {
      await axios.post(`${API}/integrations/dropbox/disconnect`, {}, axCfg());
      toast.success('Dropbox disconnected.');
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const runBackup = async () => {
    setRunning(true);
    const t0 = Date.now();
    toast.info('Backup running… this may take a minute.');
    try {
      const r = await axios.post(`${API}/backups/run`, {}, { ...axCfg(), timeout: 10 * 60 * 1000 });
      const secs = Math.round((Date.now() - t0) / 1000);
      toast.success(`Backup ok: ${r.data.filename} (${fmtBytes(r.data.size_bytes)}) in ${secs}s`);
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backup failed');
    }
    setRunning(false);
  };

  const download = async (row) => {
    try {
      const r = await axios.get(`${API}/backups/${row.backup_id}/download`, axCfg());
      window.open(r.data.download_url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate download link');
    }
  };

  const removeBackup = async (row) => {
    if (!window.confirm(`Delete "${row.filename}" from Dropbox? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/backups/${row.backup_id}`, axCfg());
      toast.success('Backup deleted');
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loadingStatus) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="backups-page">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900">Backup &amp; Restore</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Full disaster-recovery backups uploaded to Dropbox. Every backup contains every
          MongoDB collection plus every uploaded file. Environment:
          {' '}<span className="font-mono uppercase text-zinc-800">{status?.environment}</span>
          {' '}· Folder <span className="font-mono">{status?.backup_folder}</span>
        </p>
      </div>

      {/* --- Dropbox connection --- */}
      <div className="border border-zinc-300 bg-white p-4" data-testid="backups-dropbox-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Dropbox</div>
            <div className="mt-1 flex items-center gap-2">
              {status?.connected ? (
                <>
                  <PlugsConnected size={18} className="text-emerald-700" />
                  <span className="text-sm text-zinc-900 font-medium" data-testid="dropbox-connected">
                    Connected {status.account_email && <span className="text-zinc-500">· {status.account_email}</span>}
                  </span>
                </>
              ) : (
                <>
                  <Plugs size={18} className="text-zinc-400" />
                  <span className="text-sm text-zinc-500" data-testid="dropbox-disconnected">Not connected</span>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {status?.connected ? (
              <Button variant="outline" onClick={disconnect} data-testid="dropbox-disconnect-btn"
                      className="rounded-none">Disconnect</Button>
            ) : (
              <Button onClick={connect} disabled={!status?.has_credentials || connecting}
                      className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                      data-testid="dropbox-connect-btn">
                {connecting ? 'Redirecting…' : 'Connect Dropbox'}
              </Button>
            )}
          </div>
        </div>

        {!status?.has_credentials && (
          <div className="border-t border-zinc-200 pt-3 space-y-2">
            <p className="text-xs text-zinc-500">
              First, paste the App Key + App Secret from
              {' '}<a className="underline" target="_blank" rel="noopener noreferrer"
                     href="https://www.dropbox.com/developers/apps">Dropbox App Console</a>.
              Add this exact redirect URI in the app settings:
              {' '}<code className="text-[11px] bg-zinc-100 px-1">{process.env.REACT_APP_BACKEND_URL}/api/integrations/dropbox/callback</code>
              . Required permissions: <code className="text-[11px] bg-zinc-100 px-1">files.content.write</code>,
              <code className="text-[11px] bg-zinc-100 px-1">files.content.read</code>,
              <code className="text-[11px] bg-zinc-100 px-1">files.metadata.read</code>,
              <code className="text-[11px] bg-zinc-100 px-1">sharing.write</code>.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-zinc-500">App Key</Label>
                <Input value={appKey} onChange={(e) => setAppKey(e.target.value)}
                       className="rounded-none h-9 font-mono text-xs"
                       data-testid="dropbox-app-key" />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-zinc-500">App Secret</Label>
                <Input type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)}
                       className="rounded-none h-9 font-mono text-xs"
                       data-testid="dropbox-app-secret" />
              </div>
            </div>
            <Button onClick={saveCreds} disabled={savingCreds}
                    className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                    data-testid="dropbox-save-creds">
              {savingCreds ? 'Saving…' : 'Save credentials'}
            </Button>
          </div>
        )}
      </div>

      {/* --- Backup Now --- */}
      <div className="border border-zinc-300 bg-white p-4 flex items-center justify-between" data-testid="backups-actions-panel">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Manual backup</div>
          <p className="text-sm text-zinc-600 mt-1">
            Creates a fresh ZIP snapshot of every collection + every file and uploads it to Dropbox.
          </p>
        </div>
        <Button onClick={runBackup} disabled={running || !status?.connected}
                className="rounded-none bg-emerald-700 hover:bg-emerald-800 gap-2"
                data-testid="backup-now-btn">
          <CloudArrowUp size={16} />
          {running ? 'Running… please wait' : 'Backup Now'}
        </Button>
      </div>

      {/* --- Backup History --- */}
      <div className="border border-zinc-300 bg-white" data-testid="backups-history-panel">
        <div className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            Backup history <span className="ml-1 text-zinc-400 font-mono normal-case">({backups.length})</span>
          </span>
          <Button variant="ghost" size="sm" onClick={refreshStatus} className="rounded-none gap-1 h-7"
                  data-testid="backups-refresh-btn">
            <ArrowClockwise size={14} /> Refresh
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <th className="text-left px-4 py-2">Filename</th>
              <th className="text-left px-4 py-2">Created</th>
              <th className="text-right px-4 py-2">Size</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {backups.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400"
                      data-testid="backups-empty">No backups yet.</td></tr>
            ) : backups.map(b => (
              <tr key={b.backup_id} className="border-t border-zinc-100" data-testid={`backup-row-${b.backup_id}`}>
                <td className="px-4 py-2 font-mono text-xs">{b.filename}</td>
                <td className="px-4 py-2 text-xs">{fmtDate(b.created_at)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{fmtBytes(b.size_bytes)}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                    b.status === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                            : 'border-red-200 bg-red-50 text-red-700'
                  }`}>{b.status}</span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => download(b)} className="p-1.5 hover:bg-zinc-100 text-zinc-600"
                            title="Download" data-testid={`backup-download-${b.backup_id}`}>
                      <Download size={15} />
                    </button>
                    <button onClick={() => removeBackup(b)} className="p-1.5 hover:bg-red-50 text-red-600"
                            title="Delete" data-testid={`backup-delete-${b.backup_id}`}>
                      <Trash size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- Audit log --- */}
      <div className="border border-zinc-300 bg-white" data-testid="backups-audit-panel">
        <div className="px-4 py-2 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
          Audit log <span className="ml-1 text-zinc-400 font-mono normal-case">({audit.length})</span>
        </div>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-50 text-[10px] uppercase text-zinc-500">
                <th className="text-left px-3 py-1.5">When</th>
                <th className="text-left px-3 py-1.5">Action</th>
                <th className="text-left px-3 py-1.5">User</th>
                <th className="text-left px-3 py-1.5">Result</th>
                <th className="text-right px-3 py-1.5">Duration</th>
              </tr>
            </thead>
            <tbody>
              {audit.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-zinc-400">Nothing yet.</td></tr>
              ) : audit.map(e => (
                <tr key={e.log_id} className="border-t border-zinc-100">
                  <td className="px-3 py-1 font-mono">{fmtDate(e.at)}</td>
                  <td className="px-3 py-1">{e.action}</td>
                  <td className="px-3 py-1">{e.user_email || '—'}</td>
                  <td className="px-3 py-1">{e.result}</td>
                  <td className="px-3 py-1 text-right font-mono">{Math.max(0, e.duration_ms)}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
